/**
 * Spinner Handler
 *
 * Coordinates spinner behavior for navigation, form submission, and page lifecycle events.
 */

import { translator } from "../../translation/translator";
import { TaskScheduler } from "../../core/task-scheduler";
import { spinner } from "./spinner";
import { LoadingText } from "../../constants/LoadingText";
import { spinnerForm } from "./spinner-form";

export class SpinnerHandler {
    private isNavigatingBack: boolean;

    private pageLoadSpinnerId: string | null;

    private suppressPageLoaderResetTimer: ReturnType<typeof setTimeout> | null;

    public constructor() {
        this.isNavigatingBack = false;
        this.pageLoadSpinnerId = null;
        this.suppressPageLoaderResetTimer = null;
    }

    public setSuppressPageLoaderOnce(): void {
        document.documentElement.setAttribute("data-suppress-page-loader-once", "1");

        if (this.suppressPageLoaderResetTimer !== null) {
            clearTimeout(this.suppressPageLoaderResetTimer);
        }

        this.suppressPageLoaderResetTimer = setTimeout(() => {
            document.documentElement.setAttribute("data-suppress-page-loader-once", "0");
            this.suppressPageLoaderResetTimer = null;
        }, 1500);
    }

    public handleFormSubmit(event: Event): void {
        if (event.defaultPrevented) {
            return;
        }

        const form = spinnerForm.getEventForm(event);
        if (!(form instanceof HTMLFormElement)) {
            return;
        }

        if (form.hasAttribute("data-no-spinner")) {
            // Some data-no-spinner forms (e.g. file downloads) still trigger a
            // browser-level navigation that can fire beforeunload without ever
            // unloading the page (the response is a download, not a new
            // document). Suppress the beforeunload page loader too, or it
            // shows and is never hidden since no pageshow/popstate follows.
            this.setSuppressPageLoaderOnce();
            return;
        }

        const submitBtn = spinnerForm.getSubmitButton(form);
        if (!submitBtn) {
            return;
        }

        if (submitBtn.hasAttribute("data-no-spinner")) {
            this.setSuppressPageLoaderOnce();
            return;
        }

        if (submitBtn instanceof HTMLButtonElement) {
            spinner.showButtonLoader(submitBtn, { translateKey: "spinner_proccesing" });
            this.setSuppressPageLoaderOnce();
        }
    }

    public handleLinkClick(event: Event): void {
        const eventTarget = spinnerForm.getEventTarget(event);
        const link = spinnerForm.getClosestLink(eventTarget);

        if (!link) {
            return;
        }

        if (link.hasAttribute("data-no-spinner")) {
            // See the matching comment in handleFormSubmit(): download links
            // can fire beforeunload without an actual page unload following.
            this.setSuppressPageLoaderOnce();
            return;
        }

        const href = link.getAttribute("href");
        if (!href) {
            return;
        }

        if (href.startsWith("#") || href.startsWith("http") || link.hasAttribute("data-ajax")) {
            return;
        }

        void TaskScheduler.schedule(() => {
            if (!this.isNavigatingBack) {
                spinner.showPageLoader(null);
            }
        }, "raf");
    }

    public handleDOMReady(): void {
        void TaskScheduler.schedule(() => {
            spinner.hidePageLoader();
        }, "dom");
    }

    public handlePopState(): void {
        this.isNavigatingBack = true;
        spinner.hidePageLoader();
    }

    public handleBeforeUnload(): void {
        if (document.documentElement.getAttribute("data-suppress-page-loader-once") === "1") {
            document.documentElement.setAttribute("data-suppress-page-loader-once", "0");
            if (this.suppressPageLoaderResetTimer !== null) {
                clearTimeout(this.suppressPageLoaderResetTimer);
                this.suppressPageLoaderResetTimer = null;
            }
            return;
        }

        if (!this.isNavigatingBack) {
            this.pageLoadSpinnerId = spinner.showPageLoader(null) || null;
        }
    }

    public handlePageShow(event: PageTransitionEvent): void {
        this.isNavigatingBack = false;

        if (event.persisted) {
            spinner.hidePageLoader();
            if (this.pageLoadSpinnerId) {
                spinner.hide(this.pageLoadSpinnerId);
                this.pageLoadSpinnerId = null;
            }
        }
    }

    public handlePageHide(event: PageTransitionEvent): void {
        if (event.persisted) {
            spinner.hideAll();
        }
    }

    public handleVisibilityChange(): void {
        if (!document.hidden) {
            void TaskScheduler.schedule(() => {
                if (spinner.isShowing("page-loader")) {
                    spinner.hidePageLoader();
                }
            }, "raf");
        }
    }
}

const FormSpinners = {
    showButtonSpinner(button: HTMLButtonElement, message: string | null = null): void {
        if (!(button instanceof HTMLButtonElement)) {
            return;
        }

        if (typeof message === "string") {
            spinner.showButtonLoader(button, { message, translateKey: "spinner_proccesing" });
            return;
        }

        spinner.showButtonLoader(button, { translateKey: "spinner_proccesing" });
    },

    hideButtonSpinner(button: HTMLButtonElement): void {
        if (!(button instanceof HTMLButtonElement)) {
            return;
        }

        spinner.hideButtonLoader(button);
    },

    showFormSpinner(form: HTMLFormElement, message: string | null = null): string | void {
        const defaultMessage = message || translator?.getTranslation("submitting") || "Submitting data...";
        return spinner.showFormLoader(form, defaultMessage);
    },

    showPageSpinner(message: string | null = null): string | void {
        const defaultMessage = message || translator?.getTranslation("loading") || LoadingText.LOADING;
        return spinner.showPageLoader(defaultMessage);
    },

    hidePageSpinner(): void {
        spinner.hidePageLoader();
    },
};

export { FormSpinners };
